import { alpha, createTheme } from '@mui/material/styles';

/** Architectural Monolith — align with :root in index.css */
const TOKENS = {
  primaryNavy: '#00081e',
  primaryContainer: '#0a1f44',
  surface: '#f7f9fb',
  surfaceLow: '#f2f4f6',
  surfaceLowest: '#ffffff',
  surfaceHigh: '#e6e8ea',
  surfaceHighest: '#e0e3e5',
  onSurface: '#191c1e',
  onSurfaceVariant: '#44464e',
  primaryFixed: '#d9e2ff',
  tertiaryContainer: '#002705',
  tertiaryFixed: '#a3f69c',
  onTertiaryFixedVariant: '#005312',
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    primaryContainer: string;
    surfaces: {
      base: string;
      low: string;
      lowest: string;
      high: string;
      highest: string;
    };
    mainColors: {
      primary: string;
      secondary: string;
      black: string;
      white: string;
      green: string;
      blue: string;
      background: string;
      background1: string;
      backgroundOpacity1: string;
      backgroundOverlay: string;
    };
    greyShades: {
      grey1: string;
      grey2: string;
      grey3: string;
      border1: string;
      border2: string;
    };
    glass: {
      primary: string;
      secondary: string;
      border: string;
      overlay: string;
      shadow: string;
    };
  }
  interface PaletteOptions {
    primaryContainer?: string;
    surfaces?: {
      base: string;
      low: string;
      lowest: string;
      high: string;
      highest: string;
    };
    mainColors: {
      primary: string;
      secondary: string;
      black: string;
      white: string;
      green: string;
      blue: string;
      background: string;
      background1: string;
      backgroundOpacity1: string;
      backgroundOverlay: string;
    };
    greyShades: {
      grey1: string;
      grey2: string;
      grey3: string;
      border1: string;
      border2: string;
    };
    glass: {
      primary: string;
      secondary: string;
      border: string;
      overlay: string;
      shadow: string;
    };
  }
}

const manrope = '"Manrope", system-ui, sans-serif';
const bodySans = '"IBM Plex Sans", system-ui, sans-serif';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: TOKENS.primaryNavy,
      dark: TOKENS.primaryContainer,
      light: TOKENS.primaryFixed,
      contrastText: '#ffffff',
    },
    secondary: {
      main: TOKENS.primaryContainer,
      contrastText: '#ffffff',
    },
    success: {
      main: TOKENS.onTertiaryFixedVariant,
      contrastText: TOKENS.tertiaryFixed,
    },
    error: {
      main: '#f50057',
    },
    background: {
      default: TOKENS.surface,
      paper: TOKENS.surfaceLowest,
    },
    text: {
      primary: TOKENS.onSurface,
      secondary: TOKENS.onSurfaceVariant,
      disabled: alpha(TOKENS.onSurface, 0.38),
    },
    primaryContainer: TOKENS.primaryContainer,
    surfaces: {
      base: TOKENS.surface,
      low: TOKENS.surfaceLow,
      lowest: TOKENS.surfaceLowest,
      high: TOKENS.surfaceHigh,
      highest: TOKENS.surfaceHighest,
    },
    mainColors: {
      primary: TOKENS.primaryNavy,
      secondary: TOKENS.primaryContainer,
      black: TOKENS.onSurface,
      white: '#ffffff',
      green: TOKENS.onTertiaryFixedVariant,
      blue: '#1a4b8c',
      background: TOKENS.surface,
      background1: TOKENS.surfaceLowest,
      backgroundOpacity1: 'rgba(255, 255, 255, 0.72)',
      backgroundOverlay: 'rgba(25, 28, 30, 0.04)',
    },
    greyShades: {
      grey1: TOKENS.onSurfaceVariant,
      grey2: '#5c5f66',
      grey3: '#7a7d85',
      border1: alpha(TOKENS.onSurface, 0.15),
      border2: alpha(TOKENS.onSurface, 0.08),
    },
    glass: {
      primary: 'rgba(247, 249, 251, 0.6)',
      secondary: alpha(TOKENS.primaryNavy, 0.06),
      border: 'transparent',
      overlay: 'rgba(255, 255, 255, 0.4)',
      shadow: alpha(TOKENS.onSurface, 0.04),
    },
  },
  typography: {
    fontFamily: bodySans,
    h1: {
      fontFamily: manrope,
      fontSize: '2rem',
      lineHeight: 1.2,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: TOKENS.onSurface,
    },
    h2: {
      fontFamily: manrope,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: TOKENS.onSurface,
    },
    h3: {
      fontFamily: manrope,
      fontSize: '1.5rem',
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: TOKENS.onSurface,
    },
    h4: {
      fontSize: '1.25rem',
      lineHeight: 1.3,
      fontWeight: 600,
      color: TOKENS.onSurface,
    },
    h5: {
      fontSize: '1rem',
      lineHeight: 1.35,
      fontWeight: 600,
      color: TOKENS.onSurface,
    },
    h6: {
      fontSize: '0.875rem',
      lineHeight: 1.35,
      fontWeight: 600,
      color: TOKENS.onSurface,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      color: TOKENS.onSurface,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: TOKENS.onSurfaceVariant,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: TOKENS.onSurfaceVariant,
    },
    overline: {
      fontSize: '0.625rem',
      lineHeight: 1.4,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: TOKENS.onSurfaceVariant,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: TOKENS.surface,
          color: TOKENS.onSurface,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          const isPrimaryContained =
            ownerState.variant === 'contained' &&
            (ownerState.color === 'primary' || ownerState.color === undefined);
          const isSuccessContained =
            ownerState.variant === 'contained' &&
            ownerState.color === 'success';

          const base = {
            borderRadius: '6px',
            textTransform: 'none' as const,
            fontWeight: 600,
          };

          if (isPrimaryContained) {
            return {
              ...base,
              padding: '16px 32px',
              // `&&` beats Tailwind preflight `button { color: inherit }` on same element
              '&&': {
                color: '#ffffff',
                background: `linear-gradient(135deg, ${TOKENS.primaryNavy} 0%, ${TOKENS.primaryContainer} 100%)`,
                '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                  color: 'currentColor',
                },
              },
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                '&&': {
                  background: `linear-gradient(135deg, ${alpha(
                    TOKENS.primaryNavy,
                    0.92
                  )} 0%, ${alpha(TOKENS.primaryContainer, 0.95)} 100%)`,
                  color: '#ffffff',
                },
              },
              '&.Mui-disabled': {
                background: alpha(TOKENS.primaryNavy, 0.38),
                color: alpha('#ffffff', 0.7),
              },
              '& .MuiLoadingButton-loadingIndicator': {
                color: '#ffffff',
              },
            };
          }

          if (isSuccessContained) {
            return {
              ...base,
              padding: '16px 32px',
              backgroundColor: TOKENS.onTertiaryFixedVariant,
              color: TOKENS.tertiaryFixed,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: alpha(TOKENS.onTertiaryFixedVariant, 0.9),
              },
              '& .MuiLoadingButton-loadingIndicator': {
                color: TOKENS.tertiaryFixed,
              },
            };
          }

          return {
            ...base,
            '& .MuiLoadingButton-loadingIndicator': {
              color: 'currentColor',
            },
          };
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.greyShades.grey1,
        }),
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
        variant: 'filled',
        color: 'primary',
      },
      styleOverrides: {
        root: {
          gap: '4px',
          borderRadius: '9999px',
          '& .MuiChip-icon': {
            fontSize: '14px',
            paddingLeft: '4px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surfaces.lowest,
          backgroundImage: 'none',
          borderRadius: '12px',
          boxShadow: 'none',
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surfaces.lowest,
          boxShadow: 'none',
          '& .MuiCardHeader-root': {
            color: theme.palette.text.primary,
            borderBottom: 'none',
            paddingBottom: theme.spacing(1),
            '& .MuiCardHeader-avatar': {
              i: {
                fontSize: '20px',
              },
            },
            '& .MuiTypography-root': {
              fontSize: '20px',
              fontWeight: 600,
              fontFamily: manrope,
            },
          },
          '& .MuiCardContent-root': {
            paddingTop: '16px',
            paddingBottom: '16px',
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          width: '240px',
          maxWidth: '100%',
          '&.MuiFormControl-fullWidth': {
            width: '100%',
          },
          '& .MuiInputLabel-root': {
            color: theme.palette.greyShades.grey1,
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.surfaces.highest,
            borderRadius: '4px',
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.surfaces.lowest,
              boxShadow: `0 0 0 2px ${alpha(TOKENS.primaryNavy, 0.2)}`,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.palette.surfaces.highest, 0.6),
            },
          },
          '& .MuiInputBase-input': {
            '&.Mui-disabled': {
              WebkitTextFillColor: theme.palette.greyShades.grey3,
            },
          },
          '@media screen and (max-width: 768px)': {
            width: '100%',
          },
        }),
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          width: '240px',
          maxWidth: '100%',
          borderRadius: '6px',
          '& .MuiSelect-icon': {
            color: theme.palette.greyShades.grey1,
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.surfaces.highest,
            borderRadius: '4px',
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.surfaces.lowest,
              boxShadow: `0 0 0 2px ${alpha(TOKENS.primaryNavy, 0.2)}`,
            },
          },
          '@media screen and (max-width: 768px)': {
            width: '100%',
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiDialog-paper': {
            minWidth: '360px',
            borderRadius: '12px',
            boxShadow: `0 8px 40px ${alpha(TOKENS.onSurface, 0.04)}`,
            backgroundImage: 'none',
          },
        },
      },
    },
    MuiDivider: {
      defaultProps: {
        orientation: 'horizontal',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.greyShades.border2,
          marginTop: '16px',
          marginBottom: '16px',
          width: '100%',
        }),
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiStepLabel-iconContainer': {
            '& .MuiSvgIcon-root': {
              color: theme.palette.surfaces.high,
              '&.Mui-completed': {
                color: theme.palette.primary.main,
              },
              '&.Mui-active': {
                color: theme.palette.primary.main,
              },
            },
          },
          '& .MuiStepLabel-label': {
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&.Mui-active': {
              color: theme.palette.text.primary,
            },
            '&.Mui-completed': {
              color: theme.palette.text.secondary,
            },
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTab-root': {
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          gap: '8px',
          '& .MuiFormControlLabel-label': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiCheckbox-root': {
            '&.Mui-disabled': {
              color: theme.palette.greyShades.grey3,
            },
          },
          '& .MuiFormControlLabel-label': {
            color: theme.palette.text.primary,
            '&.Mui-disabled': {
              color: theme.palette.greyShades.grey3,
            },
          },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.greyShades.grey1,
          '&.Mui-disabled': {
            color: theme.palette.greyShades.grey3,
          },
          '&.Mui-checked': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.greyShades.grey1,
          '&.Mui-checked': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: TOKENS.primaryFixed,
        },
        bar: {
          borderRadius: 4,
          backgroundColor: TOKENS.primaryNavy,
        },
      },
    },
  },
});

export default theme;
